import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { Calculator, Info, Award, Package, Star, Trophy, Users, X, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import { PageLayout } from '../../components/layout/PageLayout';
import { PageTitle } from '../../components/layout/PageTitle';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/utils';
import { Tabs } from 'antd';
import { AdSense } from '../../components/ads/AdSense';
import { useI18n } from '../../contexts/I18nContext';

// Interface pour typer les données du formulaire
interface FormData {
  crCards: number;
  '3sCards': number;
  '2sCards': number;
  '1sCards': number;
  '4dCards': number;
  rareCards: number;
  shinyCards: number;    // ✧ - Cartes shiny simples
  shiny2Cards: number;   // ✧✧ - Cartes shiny double

  packsOpened: number;
  [key: string]: number; // Index signature pour permettre l'accès dynamique
}

// Interface pour les cartes de l'utilisateur
interface UserCardWithRarity {
  quantity: number;
  cards: {
    rarity: string;
  };
}

interface UserTrophy {
  value: number;
}

// Interface pour les résultats calculés
interface ResultData {
  text: string;
  lessThanProbability: number;
  moreThanProbability: number;
  expectedPacks: number;
  expectedCards: number;
  luckRating: number; // 0-4, où 0 est très malchanceux et 4 est très chanceux
  luckText: string;
}

// Interface pour les scores du classement
interface LuckScore {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  score: number;
  rating?: number;
  rating_text: string;
  packs_opened: number;
  god_packs?: number;
  crown_cards?: number;
  three_star_cards?: number;
  two_star_cards?: number;
  one_star_cards?: number;
  four_diamond_cards?: number;
  created_at: string;
  updated_at?: string;
  rank: number;
}

// Définition des couleurs pour chaque type de carte
const cardColors: Record<string, string> = {
  crCards: 'from-yellow-300 to-yellow-500',
  '3sCards': 'from-purple-300 to-purple-500',
  '2sCards': 'from-blue-300 to-blue-500',
  '1sCards': 'from-green-300 to-green-500',
  '4dCards': 'from-red-300 to-red-500',
  shinyCards: 'from-cyan-300 to-cyan-500',
  shiny2Cards: 'from-indigo-300 to-indigo-500',
  rareCards: 'from-pink-300 to-pink-500',
};

// Définition des icônes pour chaque type de carte
const cardIcons: Record<string, string> = {
  crCards: '👑',
  '3sCards': '⭐⭐⭐',
  '2sCards': '⭐⭐',
  '1sCards': '⭐',
  '4dCards': '♦♦♦♦',
  shinyCards: '✧',
  shiny2Cards: '✧✧',
  rareCards: '🌟',
};

// Définition des noms pour chaque type de carte
const cardNames: Record<string, string> = {
  crCards: 'Couronne',
  '3sCards': 'Triple Étoile',
  '2sCards': 'Double Étoile',
  '1sCards': 'Étoile Simple',
  '4dCards': 'Quadruple Diamant',
  shinyCards: 'Shiny Simple',
  shiny2Cards: 'Shiny Double',
  rareCards: 'Packs Rares ("God packs")',
};

// Mapping des raretés de la base de données vers les clés du formulaire
const rarityToFormKey: Record<string, string> = {
  // Symboles Unicode étoiles pleines
  '★★★': '3sCards',
  '★★': '2sCards',
  '★': '1sCards',
  // Symboles Unicode étoiles vides
  '☆☆☆': '3sCards',
  '☆☆': '2sCards',
  '☆': '1sCards',
  // Symboles Unicode diamants pleins
  '♦♦♦♦': '4dCards',
  // Symboles Unicode diamants vides
  '♢♢♢♢': '4dCards',
  // Symboles textuels étoiles
  '***': '3sCards',
  '**': '2sCards',
  '*': '1sCards',
  // Symboles textuels diamants
  'diamond': '4dCards',
  '4 diamonds': '4dCards',
  '4 Diamonds': '4dCards',
  'four diamonds': '4dCards',
  'Four Diamonds': '4dCards',
  '4d': '4dCards',
  '4D': '4dCards',
  // Couronne
  'crown': 'crCards',
  'Crown': 'crCards',
  '👑': 'crCards',
  '♛': 'crCards',
  // Shiny
  '✧': 'shinyCards',
  '✧✧': 'shiny2Cards',
  'shiny': 'shinyCards',
  'Shiny': 'shinyCards',
  'shiny double': 'shiny2Cards',
  'Shiny Double': 'shiny2Cards'
};

// Définition des coefficients de pondération pour chaque type de carte
const weightCoefficients: Record<string, number> = {
  rareCards: 1.0,    // God packs (plus important)
  crCards: 0.8,      // Couronnes
  shiny2Cards: 0.7,  // Shiny double
  '3sCards': 0.6,    // Triple étoile
  shinyCards: 0.5,   // Shiny simple
  '2sCards': 0.4,    // Double étoile
  '1sCards': 0.2,    // Simple étoile
  '4dCards': 0.1,    // Quadruple diamant (moins important)
};

export const PokemonLuck: React.FC = () => {
  const { user } = useAuth();
  const { t, getPathForLang } = useI18n();
  const [formData, setFormData] = useState<FormData>({
    crCards: 0,
    '3sCards': 0,
    '2sCards': 0,
    '1sCards': 0,
    '4dCards': 0,
    rareCards: 0,
    shinyCards: 0,
    shiny2Cards: 0,
    packsOpened: 0
  });
  const [results, setResults] = useState<Record<string, ResultData>>({});
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'calculator' | 'leaderboard'>('calculator');
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<1 | 2>(1);
  const [leaderboard, setLeaderboard] = useState<LuckScore[]>([]);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [selectedScore, setSelectedScore] = useState<LuckScore | null>(null);

  // Fonction pour charger les cartes de l'utilisateur
  const loadUserCards = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Récupérer les cartes de l'utilisateur avec leur rareté en utilisant l'approche suggérée
      const { data, error } = await supabase
        .from('user_cards')
        .select('cards(rarity), quantity')
        .eq('user_id', user.id)
        .gt('quantity', 0);

      if (error) {
        console.error('Erreur lors du chargement des cartes:', error);
        setError('Erreur lors du chargement de vos cartes. Veuillez réessayer.');
        setLoading(false);
        return;
      }

      console.log("Cartes récupérées:", data);

      // Initialiser le comptage des cartes
      const cardCounts: FormData = {
        crCards: 0,
        '3sCards': 0,
        '2sCards': 0,
        '1sCards': 0,
        '4dCards': 0,
        shinyCards: 0,
        shiny2Cards: 0,
        rareCards: 0,
        packsOpened: 0
      };

      // Utiliser l'approche de réduction pour regrouper par rareté
      if (data && Array.isArray(data)) {
        // Créer un objet qui contient le total des cartes par rareté
        const result = data.reduce((acc: Record<string, number>, item: any) => {
          if (item && item.cards && item.quantity) {
            const rarity = item.cards.rarity;
            if (rarity) {
              // Log pour déboguer les raretés
              console.log(`Rareté trouvée: "${rarity}" (type: ${typeof rarity})`);
              
              acc[rarity] = (acc[rarity] || 0) + item.quantity;
            }
          }
          return acc;
        }, {});

        console.log('Total des cartes par rareté:', result);

        // Convertir les raretés en clés de formulaire
        Object.entries(result).forEach(([rarity, quantity]) => {
          // Log pour déboguer le mapping des raretés
          console.log(`Conversion de la rareté: "${rarity}" -> formKey: "${rarityToFormKey[rarity] || 'non mappé'}"`);
          
          const formKey = rarityToFormKey[rarity];
          if (formKey) {
            cardCounts[formKey] += quantity;
            console.log(`Ajout de ${quantity} cartes à ${formKey} (rareté: ${rarity})`);
          } else {
            console.log(`Rareté non reconnue: "${rarity}"`);
            
            // Traitement spécial pour les diamants
            if (rarity === '♢♢♢♢' || rarity === '♦♦♦♦') {
              console.log(`Rareté 4 diamants détectée: "${rarity}" -> "4dCards"`);
              cardCounts['4dCards'] += quantity;
            }
            // Ne pas compter les autres variantes de diamants dans 4dCards
          }
        });
      }

      console.log("Comptage final des cartes:", cardCounts);

      // Mettre à jour le formulaire avec les données récupérées
      setFormData(cardCounts);

    } catch (error) {
      console.error('Erreur lors du chargement des cartes:', error);
      setError('Erreur lors du chargement de vos cartes. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Charger les cartes au montage du composant
  useEffect(() => {
    loadUserCards();
  }, [user]);

  // Fonction pour mettre à jour les valeurs du formulaire
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    
    // Convertir en nombre ou utiliser 0 si vide
    const numValue = value === '' ? 0 : parseInt(value, 10);
    
    // Mettre à jour le formulaire
    setFormData(prev => ({
      ...prev,
      [id]: numValue
    }));
  };

  // Fonction pour calculer la chance pour chaque type de carte
  const calculateAllLuck = () => {
    const { packsOpened } = formData;
    
    if (packsOpened === 0) {
      setError(t('pokemon_luck.form.error_no_packs'));
      return;
    }
    
    if (packsOpened > 10000) {
      setError('Le nombre de packs est trop grand. Veuillez limiter la requête à 10 000 packs.');
      return;
    }
    
    setError('');
    
    calculateLuck(packsOpened, 'crown');
    calculateLuck(packsOpened, 'three star');
    calculateLuck(packsOpened, 'two star');
    calculateLuck(packsOpened, 'one star');
    calculateLuck(packsOpened, 'four diamond');
    calculateLuck(packsOpened, 'shiny');
    calculateLuck(packsOpened, 'shiny double');
    calculateLuck(packsOpened, 'rare');

    // Calculer la note globale après avoir calculé toutes les chances
    calculateGlobalRating();
  };

  // Fonction pour calculer la chance pour un type de carte spécifique
  const calculateLuck = (packsOpened: number, cardType: string) => {
    const rarePackRate = 1/2000;
    const normalPackRate = 1 - rarePackRate;
    const cardsPerPack = 5;
    const numSeries = 3;
    const typeKeys: Record<string, string> = {
      'crown': 'crCards', 
      'three star': '3sCards', 
      'two star': '2sCards', 
      'one star': '1sCards', 
      'rare': 'rareCards', 
      'four diamond': '4dCards',
      'shiny': 'shinyCards',
      'shiny double': 'shiny2Cards'
    };
    const rarity = typeKeys[cardType];

    // Définition des taux pour chaque type de carte
    interface RateStructure {
      [key: string]: {
        [key: string]: {
          [key: string]: number;
        };
      };
    }

    // Vérifier si l'utilisateur a des cartes shiny pour déterminer les probabilités à utiliser
    const hasShinyCards = formData.shinyCards > 0 || formData.shiny2Cards > 0;
    
    const rates: RateStructure = hasShinyCards ? {
      // Probabilités AVEC shiny (nouvelles)
      'norm': {
        '4th': {'crCards': 0.040/100, '3sCards': 0.220/100, '2sCards': 0.500/100, '1sCards': 2.572/100, '4dCards': 1.666/100, 'shinyCards': 0.714/100, 'shiny2Cards': 0.333/100},
        '5th': {'crCards': 0.160/100, '3sCards': 0.890/100, '2sCards': 2.000/100, '1sCards': 10.288/100, '4dCards': 6.664/100, 'shinyCards': 2.857/100, 'shiny2Cards': 1.333/100}
      },
      'rare': {
        'a1': {'crCards': 3.846/100, '3sCards': 3.846/100, '2sCards': 47.368/100, '1sCards': 42.105/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100},
        'a1a': {'crCards': 5.555/100, '3sCards': 5.555/100, '2sCards': 55.555/100, '1sCards': 33.333/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100},
        'a2': {'crCards': 5.263/100, '3sCards': 5.263/100, '2sCards': 46.153/100, '1sCards': 46.153/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100}
      }
    } : {
      // Probabilités SANS shiny (anciennes)
      'norm': {
        '4th': {'crCards': 0.040/100, '3sCards': 0.220/100, '2sCards': 0.500/100, '1sCards': 2.572/100, '4dCards': 1.666/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100},
        '5th': {'crCards': 0.160/100, '3sCards': 0.890/100, '2sCards': 2.000/100, '1sCards': 10.288/100, '4dCards': 6.666/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100}
      },
      'rare': {
        'a1': {'crCards': 3.846/100, '3sCards': 3.846/100, '2sCards': 47.368/100, '1sCards': 42.105/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100},
        'a1a': {'crCards': 5.555/100, '3sCards': 5.555/100, '2sCards': 55.555/100, '1sCards': 33.333/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100},
        'a2': {'crCards': 5.263/100, '3sCards': 5.263/100, '2sCards': 46.153/100, '1sCards': 46.153/100, '4dCards': 0.000/100, 'shinyCards': 0.000/100, 'shiny2Cards': 0.000/100}
      }
    };

    const rate = (cardType === 'rare') ? rarePackRate :
      normalPackRate * (1 - (1 - rates['norm']['4th'][rarity]) * 
                            (1 - rates['norm']['5th'][rarity])) + 
      rarePackRate * (1 - (1 - (rates['rare']['a1'][rarity] + 
                                rates['rare']['a1a'][rarity] +
                                rates['rare']['a2'][rarity]) / numSeries) ** cardsPerPack);

    const expCardsPerPack = (cardType === 'rare') ? rate :
      normalPackRate * (rates['norm']['4th'][rarity] + rates['norm']['5th'][rarity]) +
      rarePackRate * ((rates['rare']['a1'][rarity] + 
                      rates['rare']['a1a'][rarity] +
                      rates['rare']['a2'][rarity]) / numSeries) * cardsPerPack;

    const numCards = formData[rarity];
    
    if (isNaN(numCards)) {
      return;
    }

    if (packsOpened === 0) {
      setResults(prev => ({
        ...prev,
        [rarity]: {
          text: t('pokemon_luck.form.error_no_packs'),
          lessThanProbability: 0,
          moreThanProbability: 0,
          expectedPacks: 0,
          expectedCards: 0,
          luckRating: 2,
          luckText: t('pokemon_luck.results.average')
        }
      }));
      return;
    }

    if (numCards > packsOpened * 5) {
      setResults(prev => ({
        ...prev,
        [rarity]: {
          text: t('pokemon_luck.form.error_suspicious_values'),
          lessThanProbability: 0,
          moreThanProbability: 0,
          expectedPacks: 0,
          expectedCards: 0,
          luckRating: 2,
          luckText: t('pokemon_luck.form.error_invalid_data')
        }
      }));
      return;
    } 
    
    if (numCards > 1000) {
      setResults(prev => ({
        ...prev,
        [rarity]: {
          text: t('pokemon_luck.form.error_too_many_cards'),
          lessThanProbability: 0,
          moreThanProbability: 0,
          expectedPacks: 0,
          expectedCards: 0,
          luckRating: 2,
          luckText: t('pokemon_luck.form.error_invalid_data')
        }
      }));
      return;
    } 
    
    if (numCards < 0) {
      setResults(prev => ({
        ...prev,
        [rarity]: {
          text: t('pokemon_luck.form.error_invalid_number'),
          lessThanProbability: 0,
          moreThanProbability: 0,
          expectedPacks: 0,
          expectedCards: 0,
          luckRating: 2,
          luckText: t('pokemon_luck.form.error_invalid_data')
        }
      }));
      return;
    }

    const { exactProbability, lessThanProbability } = getProbabilities(packsOpened, numCards, rate);
    const moreThanProbability = 1 - exactProbability - lessThanProbability;
    const atLeastProbability = 1 - lessThanProbability;
    const expectedPacks = numCards > 0 ? numCards / expCardsPerPack : 0;
    const expectedCards = packsOpened * expCardsPerPack;

    const cardPlural = numCards > 1 ? 's' : '';
    const packPlural = packsOpened > 1 ? 's' : '';
    const item = cardType === 'rare' ? 'pack' : 'carte';

    const goodText = [
      t('pokemon_luck.results.lucky'),
      t('pokemon_luck.results.lucky'),
      t('pokemon_luck.results.very_lucky'),
      t('pokemon_luck.results.incredibly_lucky')
    ];
    
    const badText = [
      t('pokemon_luck.results.unlucky'),
      t('pokemon_luck.results.unlucky'),
      t('pokemon_luck.results.very_unlucky'),
      t('pokemon_luck.results.very_unlucky')
    ];
    
    const avgText = t('pokemon_luck.results.average');

    const thresh = [1/Math.E, 20/100, 10/100, 5/100];
    let flavorText;
    let luckRating = 2; // Valeur par défaut (moyenne)
    
    if (exactProbability < thresh[0]) {
      if (atLeastProbability < thresh[3]) {
        flavorText = goodText[3];
        luckRating = 4;
      } else if (atLeastProbability < thresh[2]) {
        flavorText = goodText[2];
        luckRating = 3;
      } else if (atLeastProbability < thresh[1]) {
        flavorText = goodText[1];
        luckRating = 3;
      } else if (atLeastProbability < thresh[0]) {
        flavorText = goodText[0];
        luckRating = 3;
      } else if ((atLeastProbability < 1-thresh[0]) || (moreThanProbability < thresh[0])) {
        flavorText = avgText;
        luckRating = 2;
      } else if (moreThanProbability < 1-thresh[1]) {
        flavorText = badText[0];
        luckRating = 1;
      } else if (moreThanProbability < 1-thresh[2]) {
        flavorText = badText[1];
        luckRating = 1;
      } else if (moreThanProbability < 1-thresh[3]) {
        flavorText = badText[2];
        luckRating = 0;
      } else {
        flavorText = badText[3];
        luckRating = 0;
      }
    } else {
      flavorText = avgText;
      luckRating = 2;
    }

    const cardTypeNames: Record<string, string> = {
      'crown': '👑',
      'three star': '⭐⭐⭐',
      'two star': '⭐⭐',
      'one star': '⭐',
      'four diamond': '♦♦♦♦',
      'rare': 'rare ("god")'
    };

    const cardTypeName = cardTypeNames[cardType];

    let resultText = `Chance d'avoir moins de ${numCards} ${cardTypeName}${cardPlural} dans ${packsOpened} pack${packPlural}: ${(Math.abs(lessThanProbability) * 100).toFixed(2)}%\n`;
    resultText += `Chance d'avoir plus de ${numCards} ${cardTypeName}${cardPlural} dans ${packsOpened} pack${packPlural}: ${(Math.abs(moreThanProbability) * 100).toFixed(2)}%\n\n`;
    
    if (numCards > 0) {
      resultText += `Nombre attendu de packs pour ${numCards} ${cardTypeName}${cardPlural}: ${expectedPacks.toFixed(1)}\n`;
    }
    
    resultText += `Nombre attendu de ${cardTypeName} ${item}s dans ${packsOpened} pack${packPlural}: ${expectedCards.toFixed(1)}\n\n`;
    resultText += `Votre chance de ${cardTypeName} ${item} est ${flavorText}`;

    setResults(prev => ({
      ...prev,
      [rarity]: {
        text: resultText,
        lessThanProbability,
        moreThanProbability,
        expectedPacks,
        expectedCards,
        luckRating,
        luckText: flavorText
      }
    }));
  };

  // Fonctions mathématiques pour les calculs de probabilité
  const getProbabilities = (n: number, k: number, p: number) => {
    const exactProbability = binomialProbability(n, k, p);
    let lessThanProbability = 0;
    for (let i = 0; i < k; i++) {
      lessThanProbability += binomialProbability(n, i, p);
    }
    return { exactProbability, lessThanProbability };
  };

  const combination = (n: number, k: number) => {
    if (k > n) return 0;
    if (k === 0 || k === n) return 1;
    if (k > n - k) k = n - k;
    let result = 1;
    for (let i = 1; i <= k; i++) {
      result *= (n - (k - i)) / i;
    }
    return result;
  };

  const binomialProbability = (n: number, k: number, p: number) => {
    // P(X = k) = C(n, k) * p^k * (1-p)^(n-k)
    const nChooseK = combination(n, k);
    return nChooseK * Math.pow(p, k) * Math.pow(1 - p, n - k);
  };

  // Fonction pour générer les étoiles de chance
  const renderLuckStars = (rating: number) => {
    const stars = [];
    const maxStars = 5;
    
    for (let i = 0; i < maxStars; i++) {
      const starClass = i < rating ? 'text-yellow-500' : 'text-gray-300';
      stars.push(
        <Star 
          key={i} 
          className={`w-5 h-5 ${starClass}`} 
          fill={i < rating ? 'currentColor' : 'none'} 
        />
      );
    }
    
    return (
      <div className="flex items-center space-x-1 luck-stars">
        {stars}
      </div>
    );
  };

  // Fonction pour générer une barre de progression
  const renderProgressBar = (value: number, maxValue: number, colorClass: string) => {
    const percentage = Math.min(Math.max((value / maxValue) * 100, 0), 100);
    
    return (
      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r ${colorClass} transition-all duration-500 ease-out luck-progress-bar`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    );
  };

  // Fonction pour calculer la note globale
  const calculateGlobalRating = () => {
    let totalWeight = 0;
    let weightedSum = 0;
    let totalResults = 0;

    Object.entries(results).forEach(([key, result]) => {
      if (result && weightCoefficients[key]) {
        const weight = weightCoefficients[key];
        weightedSum += result.luckRating * weight;
        totalWeight += weight;
        totalResults++;
      }
    });

    if (totalResults === 0) return null;

    const globalRating = weightedSum / totalWeight;
    const globalLuckText = getGlobalLuckText(globalRating);

    return {
      rating: globalRating,
      text: globalLuckText,
      score: Math.round(globalRating * 20), // Note sur 100
    };
  };

  // Fonction pour obtenir le texte de chance globale
  const getGlobalLuckText = (rating: number) => {
    if (rating < 1) return t('pokemon_luck.results.very_unlucky');
    if (rating < 2) return t('pokemon_luck.results.unlucky');
    if (rating < 3) return t('pokemon_luck.results.average');
    if (rating < 4) return t('pokemon_luck.results.lucky');
    return t('pokemon_luck.results.very_lucky');
  };

  // Fonction pour charger le classement
  const loadLeaderboard = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_top_luck_scores', { limit_count: 100 });

      if (error) throw error;
      
      // Afficher les données brutes
      console.log('Données brutes du classement:', data);
      
      // Vérifier les propriétés manquantes
      if (data && data.length > 0) {
        const firstScore = data[0];
        const missingProps = [];
        
        // Liste des propriétés attendues
        const expectedProps = [
          'crown_cards', 'three_star_cards', 'two_star_cards', 
          'one_star_cards', 'four_diamond_cards', 'god_packs',
          'rating'
        ];
        
        // Vérifier chaque propriété
        for (const prop of expectedProps) {
          if (firstScore[prop] === undefined) {
            missingProps.push(prop);
          }
        }
        
        // Afficher les propriétés manquantes dans la console
        if (missingProps.length > 0) {
          console.warn('Propriétés manquantes dans les données de score:', missingProps);
          console.log('Premier score:', firstScore);
        }
      }
      
      setLeaderboard(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement du classement:', error);
    }
  };

  // Charger le classement au montage du composant
  useEffect(() => {
    loadLeaderboard();
  }, []);

  // Fonction pour publier un score
  const publishScore = async () => {
    if (!user) {
      setPublishError('Vous devez être connecté pour publier votre score.');
      return;
    }

    const globalResult = calculateGlobalRating();
    if (!globalResult) {
      setPublishError('Impossible de calculer le score global.');
      return;
    }

    setIsPublishing(true);
    setPublishError('');

    try {
      const scoreData = {
        user_id: user.id,
        username: user.email?.split('@')[0] || 'Anonyme',
        score: globalResult.score,
        rating: globalResult.rating,
        rating_text: globalResult.text,
        packs_opened: formData.packsOpened,
        god_packs: formData.rareCards,
        crown_cards: formData.crCards,
        three_star_cards: formData['3sCards'],
        two_star_cards: formData['2sCards'],
        one_star_cards: formData['1sCards'],
        four_diamond_cards: formData['4dCards']
      };

      // Vérifier si l'utilisateur a déjà un score
      const { data: existingScore } = await supabase
        .from('luck_scores')
        .select('id')
        .eq('user_id', user.id)
        .single();

      let error;
      if (existingScore) {
        // Mettre à jour le score existant
        const { error: updateError } = await supabase
          .from('luck_scores')
          .update(scoreData)
          .eq('user_id', user.id);
        error = updateError;
      } else {
        // Insérer un nouveau score
        const { error: insertError } = await supabase
          .from('luck_scores')
          .insert([scoreData]);
        error = insertError;
      }

      if (error) throw error;

      setShowPublishModal(false);
      loadLeaderboard();
      setActiveTab('leaderboard'); // Rediriger vers le classement
    } catch (error) {
      console.error('Erreur lors de la publication du score:', error);
      setPublishError('Une erreur est survenue lors de la publication du score.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Modal de publication
  const PublishScoreModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">{t('pokemon_luck.publish_modal.title')}</h3>
          <button 
            onClick={() => setShowPublishModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
            <span className="sr-only">{t('pokemon_luck.publish_modal.cancel')}</span>
          </button>
        </div>
        <p className="text-gray-600 mb-6">{t('pokemon_luck.publish_modal.description')}</p>
        
        <div className="space-y-4">
          {publishError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm">{publishError}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-2 justify-end">
            <button
              type="button"
              onClick={() => setShowPublishModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('pokemon_luck.publish_modal.cancel')}
            </button>
            <button
              type="button"
              onClick={publishScore}
              disabled={isPublishing}
              className={cn(
                "px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500",
                isPublishing && "opacity-75 cursor-not-allowed"
              )}
            >
              {isPublishing ? "..." : t('pokemon_luck.publish_modal.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Modal de détail du score
  const ScoreDetailModal = ({ score, onClose }: { score: LuckScore; onClose: () => void }) => {
    // Vérification initiale pour les scores invalides ou incomplets
    if (!score || typeof score !== 'object') {
      console.error('ScoreDetailModal - Objet score invalide');
      return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">{t('pokemon_luck.card_details.title')}</h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
                <span className="sr-only">{t('pokemon_luck.card_details.close')}</span>
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-red-500">Données de score invalides</p>
            </div>
          </div>
        </div>
      );
    }

    const formatNumber = (num: number | undefined | null) => {
      if (num === undefined || num === null) return '-';
      return num.toString();
    };

    const formatRatio = (value: number | undefined | null, packs: number | undefined | null) => {
      if (value === undefined || value === null || packs === undefined || packs === null || packs === 0) return '-';
      return (value / packs).toFixed(3);
    };

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto shadow-xl">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900">{t('pokemon_luck.card_details.title')}</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
              <span className="sr-only">{t('pokemon_luck.card_details.close')}</span>
            </button>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {score.avatar_url ? (
                  <img 
                    src={score.avatar_url} 
                    alt={`${score.username} avatar`}
                    className="h-16 w-16 rounded-full object-cover border-2 border-primary-500"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                
                <div>
                  <h4 className="text-lg font-semibold">
                    <Link 
                      to={getPathForLang(`/users/${score.username}`)}
                      className="text-primary-600 hover:text-primary-800 transition-colors"
                    >
                      {score.username}
                    </Link>
                  </h4>
                  <p className="text-sm text-gray-500">
                    {new Date(score.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                <div className="ml-auto">
                  <div className="text-2xl font-bold text-primary-600">{score.score ? score.score.toFixed(2) : '-'}/100</div>
                  <div className="text-sm text-gray-600">{score.rating_text}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h5 className="font-medium text-gray-900">{t('pokemon_luck.card_details.player')}</h5>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.score')}</span>
                      <span className="font-medium">{score.score ? score.score.toFixed(2) : '-'}/100</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.packs_opened')}</span>
                      <span className="font-medium">{score.packs_opened || '-'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <h5 className="font-medium text-gray-900">{t('pokemon_luck.form.cards.title')}</h5>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.crown')}</span>
                      <span className="font-medium">{score.crown_cards || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.triple_star')}</span>
                      <span className="font-medium">{score.three_star_cards || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.double_star')}</span>
                      <span className="font-medium">{score.two_star_cards || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.single_star')}</span>
                      <span className="font-medium">{score.one_star_cards || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.quad_diamond')}</span>
                      <span className="font-medium">{score.four_diamond_cards || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('pokemon_luck.card_details.cards.god_packs')}</span>
                      <span className="font-medium">{score.god_packs || '0'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Composant du classement
  const LeaderboardComponent = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-white flex items-center">
              <Trophy className="w-6 h-6 mr-2" />
              Classement Global
            </h2>
            <button
              onClick={() => setShowLeaderboard(false)}
              className="text-white hover:text-yellow-100"
            >
              Fermer
            </button>
          </div>
        </div>
        
        {/* Annonce AdSense unique #3 - Dans la section du classement */}
        <div className="mt-6 mb-8 flex justify-center items-center">
          <AdSense adSlot="1424794127" />
        </div>
        
        {/* Version desktop - Tableau */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rang</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joueur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Évaluation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Packs</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider lg:table-cell hidden">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leaderboard.map((score) => (
                <tr key={score.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {score.rank <= 3 ? (
                        <span className="flex items-center">
                          {score.rank === 1 && "🥇"}
                          {score.rank === 2 && "🥈"}
                          {score.rank === 3 && "🥉"}
                          {score.rank}
                        </span>
                      ) : (
                        score.rank
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {score.avatar_url ? (
                        <img 
                          src={score.avatar_url} 
                          alt={`Avatar de ${score.username}`}
                          className="h-8 w-8 rounded-full mr-3 object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <Users className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      <Link 
                        to={getPathForLang(`/users/${score.username}`)}
                        className="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors"
                      >
                        {score.username}
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{score.score ? score.score.toFixed(2) : '-'}/100</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {(() => {
                        // Fonction pour convertir le texte de rating en clé de traduction
                        const getRatingKey = (ratingText: string) => {
                          // Si le texte est vide ou null/undefined, utiliser la valeur par défaut
                          if (!ratingText) return 'average';
                          
                          const text = ratingText.toLowerCase();
                          
                          // Correspondances exactes basées sur les anciennes évaluations
                          if (text === 'excellente' || text === 'excellent') return 'incredibly_lucky';
                          if (text === 'très bonne' || text === 'très bon') return 'very_lucky';
                          if (text === 'bonne' || text === 'bon') return 'lucky';
                          if (text === 'dans la moyenne') return 'average';
                          if (text === 'en dessous de la moyenne') return 'unlucky';
                          if (text === 'mauvaise' || text === 'mauvais') return 'very_unlucky';
                          if (text === 'catastrophique') return 'very_unlucky';
                          
                          // Correspondances par mots-clés pour les cas non exacts
                          if (text.includes('excel') || text.includes('except') || text.includes('perfect')) return 'incredibly_lucky';
                          if (text.includes('très') && (text.includes('bonne') || text.includes('bon'))) return 'very_lucky';
                          if (text.includes('bonne') || text.includes('bon') || text.includes('au-dessus')) return 'lucky';
                          if (text.includes('moyenne') && !text.includes('dessous')) return 'average';
                          if (text.includes('dessous') || text.includes('below')) return 'unlucky';
                          if (text.includes('mauvais') || text.includes('catast')) return 'very_unlucky';
                          
                          // Cas spécifiques en anglais
                          if (text.includes('very') && text.includes('lucky')) return 'very_lucky';
                          if (text.includes('lucky') && !text.includes('unlucky')) return 'lucky';
                          if (text.includes('average')) return 'average';
                          if (text.includes('unlucky')) return 'unlucky';
                          if (text.includes('very') && text.includes('unlucky')) return 'very_unlucky';
                          
                          // Scores traduits directement
                          const scoreMapping: Record<number, string> = {
                            0: 'very_unlucky',    // 0-30: Mauvaise
                            1: 'unlucky',         // 30-40: En dessous de la moyenne
                            2: 'average',         // 40-50: Dans la moyenne
                            3: 'lucky',           // 50-60: Très bonne
                            4: 'incredibly_lucky' // 60+: Excellente
                          };
                          
                          // Si score.rating est disponible, l'utiliser comme fallback
                          if (score.rating !== undefined) {
                            const ratingAsNum = Math.floor(score.rating);
                            if (scoreMapping[ratingAsNum]) {
                              return scoreMapping[ratingAsNum];
                            }
                          }
                          
                          // Si le score est disponible, essayer de déterminer l'évaluation basée sur le score
                          if (score.score !== undefined) {
                            if (score.score >= 60) return 'incredibly_lucky';  // Excellente
                            if (score.score >= 50) return 'lucky';             // Très bonne
                            if (score.score >= 40) return 'average';           // Dans la moyenne
                            if (score.score >= 30) return 'unlucky';           // En dessous de la moyenne
                            if (score.score >= 0) return 'very_unlucky';       // Mauvaise
                          }
                          
                          // Valeur par défaut si rien ne correspond
                          console.log('Cas non géré pour le rating:', text);
                          return 'average';
                        };
                        
                        return t(`pokemon_luck.leaderboard.ratings.${getRatingKey(score.rating_text)}`);
                      })()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{score.packs_opened}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 lg:table-cell hidden">
                    {new Date(score.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewScoreDetails(score.id)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Version mobile - Cartes */}
        <div className="md:hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center text-xs font-medium text-gray-500 uppercase">
              <span>Classement</span>
              <span>Score</span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {leaderboard.map((score) => (
              <div key={score.id} className="p-4 hover:bg-gray-50">
                {/* Première ligne: Rang, Avatar, Nom, Score */}
                <div className="flex items-center mb-3 w-full">
                  {/* Rang avec médaille */}
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center mr-2">
                    {score.rank <= 3 ? (
                      <span className="text-lg font-bold">
                        {score.rank === 1 && "🥇"}
                        {score.rank === 2 && "🥈"}
                        {score.rank === 3 && "🥉"}
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-700 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold">
                        {score.rank}
                      </span>
                    )}
                  </div>
                  
                  {/* Avatar et nom */}
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center">
                      {score.avatar_url ? (
                        <img 
                          src={score.avatar_url} 
                          alt={`Avatar de ${score.username}`}
                          className="h-8 w-8 rounded-full mr-2 object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2 flex-shrink-0">
                          <Users className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      <div className="truncate">
                        <Link
                          to={getPathForLang(`/users/${score.username}`)}
                          className="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors truncate block"
                        >
                          {score.username}
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Score */}
                  <div className="flex-shrink-0 ml-auto pl-2">
                    <div className="text-base font-bold text-gray-900">{score.score ? score.score.toFixed(1) : '-'}</div>
                  </div>
                </div>
                
                {/* Deuxième ligne: Évaluation, Packs, Bouton détails */}
                <div className="flex flex-wrap items-center gap-y-2">
                  <div className="flex items-center mr-3">
                    <span className="px-2 py-0.5 text-xs leading-none font-medium rounded-full bg-green-100 text-green-800">
                      {(() => {
                        // Fonction pour convertir le texte de rating en clé de traduction
                        const getRatingKey = (ratingText: string) => {
                          // Si le texte est vide ou null/undefined, utiliser la valeur par défaut
                          if (!ratingText) return 'average';
                          
                          const text = ratingText.toLowerCase();
                          
                          // Correspondances exactes basées sur les anciennes évaluations
                          if (text === 'excellente' || text === 'excellent') return 'incredibly_lucky';
                          if (text === 'très bonne' || text === 'très bon') return 'very_lucky';
                          if (text === 'bonne' || text === 'bon') return 'lucky';
                          if (text === 'dans la moyenne') return 'average';
                          if (text === 'en dessous de la moyenne') return 'unlucky';
                          if (text === 'mauvaise' || text === 'mauvais') return 'very_unlucky';
                          if (text === 'catastrophique') return 'very_unlucky';
                          
                          // Correspondances par mots-clés pour les cas non exacts
                          if (text.includes('excel') || text.includes('except') || text.includes('perfect')) return 'incredibly_lucky';
                          if (text.includes('très') && (text.includes('bonne') || text.includes('bon'))) return 'very_lucky';
                          if (text.includes('bonne') || text.includes('bon') || text.includes('au-dessus')) return 'lucky';
                          if (text.includes('moyenne') && !text.includes('dessous')) return 'average';
                          if (text.includes('dessous') || text.includes('below')) return 'unlucky';
                          if (text.includes('mauvais') || text.includes('catast')) return 'very_unlucky';
                          
                          // Cas spécifiques en anglais
                          if (text.includes('very') && text.includes('lucky')) return 'very_lucky';
                          if (text.includes('lucky') && !text.includes('unlucky')) return 'lucky';
                          if (text.includes('average')) return 'average';
                          if (text.includes('unlucky')) return 'unlucky';
                          if (text.includes('very') && text.includes('unlucky')) return 'very_unlucky';
                          
                          // Scores traduits directement
                          const scoreMapping: Record<number, string> = {
                            0: 'very_unlucky',    // 0-30: Mauvaise
                            1: 'unlucky',         // 30-40: En dessous de la moyenne
                            2: 'average',         // 40-50: Dans la moyenne
                            3: 'lucky',           // 50-60: Très bonne
                            4: 'incredibly_lucky' // 60+: Excellente
                          };
                          
                          // Si score.rating est disponible, l'utiliser comme fallback
                          if (score.rating !== undefined) {
                            const ratingAsNum = Math.floor(score.rating);
                            if (scoreMapping[ratingAsNum]) {
                              return scoreMapping[ratingAsNum];
                            }
                          }
                          
                          // Si le score est disponible, essayer de déterminer l'évaluation basée sur le score
                          if (score.score !== undefined) {
                            if (score.score >= 60) return 'incredibly_lucky';  // Excellente
                            if (score.score >= 50) return 'lucky';             // Très bonne
                            if (score.score >= 40) return 'average';           // Dans la moyenne
                            if (score.score >= 30) return 'unlucky';           // En dessous de la moyenne
                            if (score.score >= 0) return 'very_unlucky';       // Mauvaise
                          }
                          
                          // Valeur par défaut si rien ne correspond
                          console.log('Cas non géré pour le rating:', text);
                          return 'average';
                        };
                        
                        return t(`pokemon_luck.leaderboard.ratings.${getRatingKey(score.rating_text)}`);
                      })()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 flex items-center">
                    <Package className="w-3 h-3 mr-1 flex-shrink-0" />
                    <span>{score.packs_opened}</span>
                  </div>
                  <button
                    onClick={() => handleViewScoreDetails(score.id)}
                    className="ml-auto px-3 py-1 text-xs font-medium bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
                  >
                    Détails
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Fonction pour charger les détails d'un score spécifique
  const loadScoreDetails = async (scoreId: string) => {
    try {
      console.log(`Tentative de récupération des détails pour le score ID: ${scoreId}`);
      
      if (!scoreId) {
        console.error('ID de score invalide');
        return null;
      }
      
      // Récupérer les détails du score
      const { data, error } = await supabase
        .from('luck_scores')
        .select('*')
        .eq('id', scoreId)
        .single();

      if (error) {
        console.error('Erreur lors du chargement des détails du score:', error);
        return null;
      }

      if (!data) {
        console.error('Aucune donnée récupérée pour le score ID:', scoreId);
        return null;
      }

      console.log('Détails du score récupérés directement:', data);
      
      // Vérifier si les données contiennent les champs nécessaires
      const requiredFields = ['packs_opened', 'crown_cards', 'three_star_cards', 'two_star_cards', 'one_star_cards', 'four_diamond_cards'];
      const missingFields = requiredFields.filter(field => data[field] === undefined);
      
      if (missingFields.length > 0) {
        console.warn('Champs manquants dans les données récupérées:', missingFields);
      }
      
      // Si packs_opened est à 0 ou undefined, essayer de le récupérer depuis le leaderboard
      if (!data.packs_opened) {
        console.warn('packs_opened est à 0 ou undefined, recherche dans le leaderboard');
        
        // Chercher le score dans le leaderboard
        const leaderboardScore = leaderboard.find(score => score.id === scoreId);
        if (leaderboardScore && leaderboardScore.packs_opened) {
          console.log('packs_opened trouvé dans le leaderboard:', leaderboardScore.packs_opened);
          data.packs_opened = leaderboardScore.packs_opened;
        }
      }
      
      return data;
    } catch (error) {
      console.error('Exception lors du chargement des détails du score:', error);
      return null;
    }
  };

  // Fonction pour gérer la vue des détails du score
  const handleViewScoreDetails = async (scoreId: string) => {
    try {
      // Trouver d'abord le score dans le leaderboard pour récupérer le username original
      const originalScore = leaderboard.find(score => score.id === scoreId);
      
      // Récupérer ensuite les détails complets depuis la base de données
      const scoreDetails = await loadScoreDetails(scoreId);
      
      if (scoreDetails && originalScore) {
        // Conserver le username original du score dans le leaderboard
        const enhancedScore = {
          ...scoreDetails,
          username: originalScore.username, // S'assurer que le username reste le même
          avatar_url: originalScore.avatar_url // Préserver aussi l'avatar
        };
        setSelectedScore(enhancedScore);
      } else if (scoreDetails) {
        setSelectedScore(scoreDetails);
      } else if (originalScore) {
        setSelectedScore(originalScore);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails du score:', error);
      // En cas d'erreur, chercher le score dans le leaderboard
      const originalScore = leaderboard.find(score => score.id === scoreId);
      if (originalScore) {
        setSelectedScore(originalScore);
      }
    }
  };

  return (
    <PageLayout 
      breadcrumbProps={{
        items: [
          { label: t('nav.home'), path: getPathForLang('/') },
          { label: t('pokemon_luck.breadcrumbs.calculator'), path: getPathForLang('/pokemon-luck') }
        ]
      }}
    >
      <Helmet>
        <title>{t('pokemon_luck.seo.title')}</title>
        <meta name="description" content={t('pokemon_luck.seo.description')} />
        <meta name="keywords" content="Pokémon TCG Pocket, calculateur de chance, probabilités, boosters, cartes rares, crown cards, god packs, étoiles, diamants" />
        <meta property="og:title" content={t('pokemon_luck.seo.title')} />
        <meta property="og:description" content={t('pokemon_luck.seo.description')} />
        <meta property="og:type" content="website" />
      
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pokemon_luck.seo.title')} />
        <meta name="twitter:description" content={t('pokemon_luck.seo.description')} />
        
      </Helmet>
      
      <PageTitle title={t('pokemon_luck.title')} />
      
      <article 
        className="max-w-7xl mx-auto"
        itemScope
        itemType="https://schema.org/Article"
      >
        <meta itemProp="headline" content={t('pokemon_luck.subtitle')} />
        <meta itemProp="description" content={t('pokemon_luck.seo.description')} />
        <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
          <div className="p-6">
            <header className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
              <div className="flex flex-col">
                <h1 
                  className="text-3xl font-bold flex items-center"
                  itemProp="name"
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-blue-600">
                    {t('pokemon_luck.subtitle')}
                  </span>
                </h1>
                <h2 className="text-xl font-semibold text-gray-700 flex items-center mt-1">
                  <Calculator className="w-5 h-5 mr-2 text-primary-500" />
                  Pokémon TCG Pocket
                </h2>
              </div>
              
              <div className="flex space-x-2 self-end md:self-auto">
                <button
                  onClick={() => setShowLeaderboard(!showLeaderboard)}
                  className={cn(
                    "px-4 py-2 rounded-md font-medium transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-primary-500",
                    showLeaderboard 
                      ? "bg-primary-600 text-white hover:bg-primary-700" 
                      : "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                  )}
                >
                  {showLeaderboard ? (
                    <>
                      <Calculator className="w-4 h-4 mr-2 inline" />
                      <span className="hidden sm:inline">{t('pokemon_luck.tabs.calculator')}</span>
                      <span className="sm:hidden">{t('pokemon_luck.tabs.calculate')}</span>
                    </>
                  ) : (
                    <>
                      <Trophy className="w-4 h-4 mr-2 inline" />
                      <span className="hidden sm:inline">{t('pokemon_luck.tabs.ranking')}</span>
                      <span className="sm:hidden">{t('pokemon_luck.tabs.ranking')}</span>
                    </>
                  )}
                </button>
                
                {!showLeaderboard && user && Object.keys(results).length > 0 && (
                  <button
                    onClick={() => setShowPublishModal(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md font-medium hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <Trophy className="w-4 h-4 mr-2 inline" />
                    <span className="hidden sm:inline">{t('pokemon_luck.tabs.publish')}</span>
                    <span className="sm:hidden">{t('pokemon_luck.tabs.publish')}</span>
                  </button>
                )}
              </div>
            </header>

            {/* Annonce AdSense unique #1 - Après le titre principal */}
            <div className="mt-6 mb-8 flex justify-center items-center">
              <AdSense adSlot="1424794127" />
            </div>

            {!showLeaderboard && step === 1 && (
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Info className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">{t('pokemon_luck.info.title')}</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          {t('pokemon_luck.info.description')}
                        </p>
                        <p className="mt-2">
                          <strong>Mode de probabilités :</strong> Le calculateur utilise automatiquement les probabilités récentes (avec cartes shiny) si vous avez des cartes shiny, sinon il utilise les probabilités d'origine du jeu.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <div className="flex items-center justify-center space-x-2 mb-4">
                      <div className="w-4 h-4 rounded-full bg-primary-600 animate-bounce" style={{ animationDelay: '0s' }}></div>
                      <div className="w-4 h-4 rounded-full bg-primary-600 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-4 h-4 rounded-full bg-primary-600 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <p className="text-center text-gray-600">{t('pokemon_luck.loading')}</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
                          <h3 className="text-white font-medium flex items-center">
                            <Package className="w-5 h-5 mr-2" />
                            {t('pokemon_luck.form.general_info')}
                          </h3>
                        </div>
                        <div className="p-5 bg-gradient-to-b from-blue-50 to-white">
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="packsOpened" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <div className="bg-blue-100 rounded-full p-1 mr-2">
                                  <Package className="w-4 h-4 text-blue-600" />
                                </div>
                                {t('pokemon_luck.form.packs_opened')}
                              </label>
                              <input
                                type="number"
                                id="packsOpened"
                                name="packsOpened"
                                min="0"
                                value={formData.packsOpened === 0 ? '' : formData.packsOpened}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-blue-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
                                placeholder={t('pokemon_luck.form.packs_opened')}
                                aria-required="true"
                                aria-label={t('pokemon_luck.form.packs_opened')}
                              />
                            </div>
                            <div>
                              <label htmlFor="rareCards" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <div className="bg-yellow-100 rounded-full p-1 mr-2">
                                  <Star className="w-4 h-4 text-yellow-600" />
                                </div>
                                Nombre de packs rares ("God packs")
                              </label>
                              <input
                                type="number"
                                id="rareCards"
                                name="rareCards"
                                min="0"
                                value={formData.rareCards === 0 ? '' : formData.rareCards}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-yellow-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                                placeholder="Entrez le nombre de packs rares"
                                aria-label="Nombre de packs rares"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3">
                          <h3 className="text-white font-medium flex items-center">
                            <Users className="w-5 h-5 mr-2" />
                            {t('pokemon_luck.form.cards.title')}
                          </h3>
                        </div>
                        <div className="p-5 bg-gradient-to-b from-purple-50 to-white">
                          <div className="space-y-4">
                            {Object.entries(cardNames)
                              .filter(([key]) => key !== 'rareCards')
                              .map(([key, name]) => {
                                const colors = {
                                  'crCards': { bg: 'bg-yellow-100', border: 'border-yellow-300', ring: 'ring-yellow-500', text: 'text-yellow-600' },
                                  '3sCards': { bg: 'bg-purple-100', border: 'border-purple-300', ring: 'ring-purple-500', text: 'text-purple-600' },
                                  '2sCards': { bg: 'bg-blue-100', border: 'border-blue-300', ring: 'ring-blue-500', text: 'text-blue-600' },
                                  '1sCards': { bg: 'bg-green-100', border: 'border-green-300', ring: 'ring-green-500', text: 'text-green-600' },
                                  '4dCards': { bg: 'bg-red-100', border: 'border-red-300', ring: 'ring-red-500', text: 'text-red-600' },
                                  'shinyCards': { bg: 'bg-cyan-100', border: 'border-cyan-300', ring: 'ring-cyan-500', text: 'text-cyan-600' },
                                  'shiny2Cards': { bg: 'bg-indigo-100', border: 'border-indigo-300', ring: 'ring-indigo-500', text: 'text-indigo-600' },
                                };
                                const color = colors[key as keyof typeof colors] || { bg: 'bg-gray-100', border: 'border-gray-300', ring: 'ring-gray-500', text: 'text-gray-600' };
                                
                                return (
                                  <div key={key}>
                                    <label htmlFor={key} className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                      <div className={`${color.bg} rounded-full p-1 mr-2`}>
                                        <span className={`text-xs font-bold ${color.text}`}>{cardIcons[key]}</span>
                                      </div>
                                      {t(`pokemon_luck.form.cards.${key === 'crCards' ? 'crown' : 
                                              key === '3sCards' ? 'triple_star' : 
                                              key === '2sCards' ? 'double_star' : 
                                              key === '1sCards' ? 'single_star' : 
                                              key === 'shinyCards' ? 'shiny_simple' :
                                              key === 'shiny2Cards' ? 'shiny_double' :
                                              'quad_diamond'}`)}
                                    </label>
                                    <input
                                      type="number"
                                      id={key}
                                      name={key}
                                      min="0"
                                      value={formData[key] === 0 ? '' : formData[key]}
                                      onChange={handleInputChange}
                                      className={`w-full px-3 py-2 border ${color.border} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:${color.ring} focus:border-transparent bg-white`}
                                      placeholder={t(`pokemon_luck.form.cards.${key === 'crCards' ? 'crown' : 
                                                          key === '3sCards' ? 'triple_star' : 
                                                          key === '2sCards' ? 'double_star' : 
                                                          key === '1sCards' ? 'single_star' : 
                                                          key === 'shinyCards' ? 'shiny_simple' :
                                                          key === 'shiny2Cards' ? 'shiny_double' :
                                                          'quad_diamond'}`)}
                                      aria-label={t(`pokemon_luck.form.cards.${key === 'crCards' ? 'crown' : 
                                                          key === '3sCards' ? 'triple_star' : 
                                                          key === '2sCards' ? 'double_star' : 
                                                          key === '1sCards' ? 'single_star' : 
                                                          key === 'shinyCards' ? 'shiny_simple' :
                                                          key === 'shiny2Cards' ? 'shiny_double' :
                                                          'quad_diamond'}`)}
                                    />
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Indicateur du mode de probabilités */}
                    <div className="flex justify-center mt-6 mb-4">
                      <div className={cn(
                        "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                        (formData.shinyCards > 0 || formData.shiny2Cards > 0)
                          ? "bg-cyan-100 text-cyan-700 border border-cyan-200"
                          : "bg-gray-100 text-gray-700 border border-gray-200"
                      )}>
                        <div className={cn(
                          "w-2 h-2 rounded-full mr-2",
                          (formData.shinyCards > 0 || formData.shiny2Cards > 0)
                            ? "bg-cyan-500"
                            : "bg-gray-500"
                        )}></div>
                        {(formData.shinyCards > 0 || formData.shiny2Cards > 0)
                          ? "Mode Shiny - Probabilités récentes"
                          : "Mode Classique - Probabilités d'origine"
                        }
                      </div>
                    </div>

                    <div className="flex justify-center mt-4 mb-8">
                      <button
                        onClick={() => {
                          calculateAllLuck();
                          if (!error) setStep(2);
                        }}
                        disabled={formData.packsOpened <= 0}
                        className={cn(
                          "px-8 py-4 rounded-lg font-medium transition-colors shadow-md",
                          "focus:outline-none focus:ring-2 focus:ring-primary-500",
                          "flex items-center justify-center text-lg",
                          formData.packsOpened <= 0
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700"
                        )}
                        aria-label={t('pokemon_luck.form.calculate')}
                      >
                        <Calculator className="w-6 h-6 mr-2" />
                        {t('pokemon_luck.form.calculate')}
                      </button>
                    </div>

                    {/* Guide pour trouver le nombre de boosters */}
                    <h4 className="text-md font-semibold text-indigo-700 mt-8 mb-3 flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      {t('pokemon_luck.guide.packs.title')}
                    </h4>

                    <section 
                      className="bg-white rounded-lg shadow-md overflow-hidden mb-6"
                      aria-labelledby="guide-boosters"
                      itemScope
                      itemType="https://schema.org/HowTo"
                    >
                      <meta itemProp="name" content={t('pokemon_luck.guide.packs.meta_title')} />
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
                        <h4 
                          id="guide-boosters"
                          className="text-white font-medium"
                          itemProp="description"
                        >
                          {t('pokemon_luck.guide.packs.step_title')}
                        </h4>
                      </div>
                      
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Étape 1 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-blue-100 px-3 py-2">
                              <h5 className="text-blue-800 font-medium flex items-center">
                                <div className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  1
                                </div>
                                {t('pokemon_luck.guide.packs.step1.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/profil.png" 
                                  alt={t('pokemon_luck.guide.packs.step1.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.packs.step1.description')}</p>
                            </div>
                          </div>
                          
                          {/* Étape 2 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-blue-100 px-3 py-2">
                              <h5 className="text-blue-800 font-medium flex items-center">
                                <div className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  2
                                </div>
                                {t('pokemon_luck.guide.packs.step2.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/trophee.png" 
                                  alt={t('pokemon_luck.guide.packs.step2.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.packs.step2.description')}</p>
                            </div>
                          </div>
                          
                          {/* Étape 3 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-blue-100 px-3 py-2">
                              <h5 className="text-blue-800 font-medium flex items-center">
                                <div className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  3
                                </div>
                                {t('pokemon_luck.guide.packs.step3.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/booster.png" 
                                  alt={t('pokemon_luck.guide.packs.step3.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.packs.step3.description')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Guide pour les packs rares */}
                    <h4 className="text-md font-semibold text-indigo-700 mt-6 mb-3 flex items-center">
                      <Star className="w-5 h-5 mr-2" />
                      {t('pokemon_luck.guide.rare_packs.title')}
                    </h4>

                    <section 
                      className="bg-white rounded-lg shadow-md overflow-hidden mb-6"
                      aria-labelledby="guide-packs-rares"
                      itemScope
                      itemType="https://schema.org/HowTo"
                    >
                      <meta itemProp="name" content={t('pokemon_luck.guide.rare_packs.meta_title')} />
                      <div className="bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-3">
                        <h4 
                          id="guide-packs-rares"
                          className="text-white font-medium"
                          itemProp="description"
                        >
                          {t('pokemon_luck.guide.rare_packs.how_to_identify')}
                        </h4>
                      </div>
                      
                      <div className="p-4">
                        <div className="space-y-4">
                          <ul className="space-y-2 text-sm text-gray-700">
                            {[1, 2, 3].map((step) => (
                              <li key={step} className="flex items-start" itemProp="step" itemScope itemType="https://schema.org/HowToStep">
                                <div className="bg-amber-400 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">{step}</div>
                                <span itemProp="text">{t(`pokemon_luck.guide.rare_packs.step${step}`)}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <div className="bg-yellow-50 p-3 rounded-lg">
                            <p className="text-xs text-amber-700 flex items-start">
                              <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-amber-600" />
                              <span>{t('pokemon_luck.guide.rare_packs.tip')}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Guide pour trouver les cartes par rareté */}
                    <h4 className="text-md font-semibold text-indigo-700 mt-6 mb-3 flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      {t('pokemon_luck.guide.cards_by_rarity.title')}
                    </h4>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3">
                        <h4 className="text-white font-medium">{t('pokemon_luck.guide.cards_by_rarity.step_title')}</h4>
                      </div>
                      
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Étape 1 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-purple-100 px-3 py-2">
                              <h5 className="text-purple-800 font-medium flex items-center">
                                <div className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  1
                                </div>
                                {t('pokemon_luck.guide.cards_by_rarity.step1.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/search.png" 
                                  alt={t('pokemon_luck.guide.cards_by_rarity.step1.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.cards_by_rarity.step1.description')}</p>
                            </div>
                          </div>
                          
                          {/* Étape 2 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-purple-100 px-3 py-2">
                              <h5 className="text-purple-800 font-medium flex items-center">
                                <div className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  2
                                </div>
                                {t('pokemon_luck.guide.cards_by_rarity.step2.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/rareté.png" 
                                  alt={t('pokemon_luck.guide.cards_by_rarity.step2.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.cards_by_rarity.step2.description')}</p>
                            </div>
                          </div>
                          
                          {/* Étape 3 */}
                          <div className="bg-gray-50 rounded-lg overflow-hidden">
                            <div className="bg-purple-100 px-3 py-2">
                              <h5 className="text-purple-800 font-medium flex items-center">
                                <div className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center mr-2">
                                  3
                                </div>
                                {t('pokemon_luck.guide.cards_by_rarity.step3.title')}
                              </h5>
                            </div>
                            <div className="p-3">
                              <div className="flex justify-center mb-2">
                                <img 
                                  src="/images/pokemon-luck/nombre-rarete.png" 
                                  alt={t('pokemon_luck.guide.cards_by_rarity.step3.image_alt')} 
                                  className="rounded-md border border-gray-200 shadow-sm"
                                  style={{ maxWidth: '100%', height: 'auto', maxHeight: '150px' }}
                                />
                              </div>
                              <p className="text-xs text-gray-600 text-center">{t('pokemon_luck.guide.cards_by_rarity.step3.description')}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-3 bg-purple-50 p-3 rounded-lg">
                          <p className="text-xs text-purple-700 flex items-start">
                            <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-purple-600" />
                            <span>{t('pokemon_luck.guide.cards_by_rarity.tip')}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {!showLeaderboard && step === 2 && (
              <>
                {/* Annonce AdSense unique #2 - Entre le formulaire et les résultats */}
                <div className="mt-6 mb-8 flex justify-center items-center">
                  <AdSense adSlot="1424794127" />
                </div>

                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-900">{t('pokemon_luck.results.title')}</h3>
                    <button
                      onClick={() => setStep(1)}
                      className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {t('pokemon_luck.form.modify_data')}
                    </button>
                  </div>

                  {error && (
                    <div className="bg-red-50 p-4 rounded-lg mb-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{t('pokemon_luck.results.error')}</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>{error}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {Object.keys(results).length > 0 && (
                    <div className="space-y-8">
                      {/* Résumé global */}
                      {calculateGlobalRating() && (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Trophy className="w-5 h-5 mr-2 text-yellow-500" />
                              {t('pokemon_luck.results.global_rating')}
                            </h4>
                          </div>
                          <div className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                              <div className="mb-4 md:mb-0">
                                <div className="text-sm text-gray-500 mb-1">{t('pokemon_luck.results.global_score')}</div>
                                <div className="text-3xl font-bold text-gray-900">{calculateGlobalRating()?.score.toFixed(2)}<span className="text-lg font-medium text-gray-500">/100</span></div>
                              </div>
                              <div className="flex items-center">
                                <div className="text-sm text-gray-500 mr-3">{t('pokemon_luck.results.rating')}:</div>
                                <div className="text-lg font-semibold text-primary-600">{calculateGlobalRating()?.text}</div>
                              </div>
                            </div>
                            
                            <div className="relative pt-1">
                              <div className="flex mb-2 items-center justify-between">
                                <div>
                                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-red-600 bg-red-200">
                                    {t('pokemon_luck.results.luck_scale.unlucky')}
                                  </span>
                                </div>
                                <div className="hidden sm:block">
                                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-yellow-600 bg-yellow-200">
                                    {t('pokemon_luck.results.luck_scale.average')}
                                  </span>
                                </div>
                                <div className="hidden sm:block">
                                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-green-600 bg-green-200">
                                    {t('pokemon_luck.results.luck_scale.lucky')}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                                    {t('pokemon_luck.results.luck_scale.incredible')}
                                  </span>
                                </div>
                              </div>
                              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200 relative">
                                {/* Marqueurs de niveau */}
                                <div className="absolute top-0 bottom-0 left-1/4 w-px bg-gray-400"></div>
                                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-400"></div>
                                <div className="absolute top-0 bottom-0 left-3/4 w-px bg-gray-400"></div>
                                
                                {/* Barre de progression */}
                                <div 
                                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                    (calculateGlobalRating()?.rating || 0) <= 1 ? 'bg-red-500' : 
                                    (calculateGlobalRating()?.rating || 0) <= 2 ? 'bg-yellow-500' : 
                                    (calculateGlobalRating()?.rating || 0) <= 3 ? 'bg-green-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${calculateGlobalRating()?.score || 0}%` }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.packs_opened')}</div>
                                <div className="text-lg font-semibold text-gray-900">{formData.packsOpened}</div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.form.cards.god_packs')}</div>
                                <div className="text-lg font-semibold text-gray-900">{formData.rareCards}</div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.best_category')}</div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {Object.entries(results).reduce((best, [key, result]) => {
                                    if (!best || (result && result.luckRating > best.rating)) {
                                      return { key, rating: result?.luckRating || 0 };
                                    }
                                    return best;
                                  }, null as { key: string; rating: number } | null)?.key 
                                    ? t(`pokemon_luck.form.cards.${Object.entries(results).reduce((best, [key, result]) => {
                                        if (!best || (result && result.luckRating > best.rating)) {
                                          return { key, rating: result?.luckRating || 0 };
                                        }
                                        return best;
                                      }, null as { key: string; rating: number } | null)?.key.toLowerCase().replace('cards', '')}`)
                                    : '-'}
                                </div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.worst_category')}</div>
                                <div className="text-lg font-semibold text-gray-900">
                                  {Object.entries(results).reduce((worst, [key, result]) => {
                                    if (!worst || (result && result.luckRating < worst.rating)) {
                                      return { key, rating: result?.luckRating || 0 };
                                    }
                                    return worst;
                                  }, null as { key: string; rating: number } | null)?.key
                                    ? t(`pokemon_luck.form.cards.${Object.entries(results).reduce((worst, [key, result]) => {
                                        if (!worst || (result && result.luckRating < worst.rating)) {
                                          return { key, rating: result?.luckRating || 0 };
                                        }
                                        return worst;
                                      }, null as { key: string; rating: number } | null)?.key.toLowerCase().replace('cards', '')}`)
                                    : '-'}
                                </div>
                              </div>
                            </div>
                            
                            {user && (
                              <div className="mt-6 flex justify-end">
                                <button
                                  onClick={() => setShowPublishModal(true)}
                                  className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md font-medium hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                  <Trophy className="w-4 h-4 mr-2 inline" />
                                  {t('pokemon_luck.tabs.publish')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Détails par type de carte */}
                      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Star className="w-5 h-5 mr-2 text-blue-500" />
                            {t('pokemon_luck.results.details_title')}
                          </h4>
                        </div>
                        <div className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {Object.entries(results).map(([key, result]) => {
                              // Déterminer la couleur en fonction de l'évaluation
                              const ratingColors = {
                                bg: result.luckRating <= 1 ? 'bg-red-50' : 
                                    result.luckRating <= 2 ? 'bg-yellow-50' : 
                                    result.luckRating <= 3 ? 'bg-green-50' : 'bg-blue-50',
                                text: result.luckRating <= 1 ? 'text-red-700' : 
                                      result.luckRating <= 2 ? 'text-yellow-700' : 
                                      result.luckRating <= 3 ? 'text-green-700' : 'text-blue-700',
                                border: result.luckRating <= 1 ? 'border-red-200' : 
                                        result.luckRating <= 2 ? 'border-yellow-200' : 
                                        result.luckRating <= 3 ? 'border-green-200' : 'border-blue-200',
                                light: result.luckRating <= 1 ? 'text-red-500' : 
                                       result.luckRating <= 2 ? 'text-yellow-500' : 
                                       result.luckRating <= 3 ? 'text-green-500' : 'text-blue-500'
                              };
                              
                              // Calculer le pourcentage pour la barre de progression
                              // Utiliser le luckRating (0-4) pour déterminer la largeur de la barre
                              // Convertir le luckRating (0-4) en pourcentage (0-100%)
                              // Ajuster pour que 0 = 0%, 1 = 25%, 2 = 50%, 3 = 75%, 4 = 100%
                              const progressPercentage = Math.min(Math.max((result.luckRating / 4) * 100, 0), 100);
                              
                              return (
                                <div key={key} className={`border rounded-lg overflow-hidden ${ratingColors.border}`}>
                                  <div className={`px-4 py-3 border-b ${ratingColors.border} ${ratingColors.bg}`}>
                                    <div className="flex justify-between items-center">
                                      <h5 className="font-semibold text-gray-900 flex items-center">
                                        <span className="mr-2">{cardIcons[key]}</span>
                                        {t(`pokemon_luck.card_details.cards.${key.toLowerCase().replace('cards', '')}`)}
                                      </h5>
                                      <div className={`text-sm font-medium ${ratingColors.text} px-2 py-1 rounded-full ${ratingColors.bg}`}>
                                        {result.luckText.charAt(0).toUpperCase() + result.luckText.slice(1)}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="p-4">
                                    <div className="flex justify-between items-center mb-2">
                                      <div className="text-sm text-gray-500">{t('pokemon_luck.results.cards_obtained')}</div>
                                      <div className="text-sm font-medium text-gray-900">{formData[key]}</div>
                                    </div>
                                    
                                    <div className="flex justify-between items-center mb-2">
                                      <div className="text-sm text-gray-500">{t('pokemon_luck.results.cards_expected')}</div>
                                      <div className="text-sm font-medium text-gray-900">{result.expectedCards.toFixed(1)}</div>
                                    </div>
                                    
                                    <div className="relative pt-1">
                                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200 relative">
                                        {/* Marqueurs de niveau */}
                                        <div className="absolute top-0 bottom-0 left-1/4 w-px bg-gray-400"></div>
                                        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gray-400"></div>
                                        <div className="absolute top-0 bottom-0 left-3/4 w-px bg-gray-400"></div>
                                        
                                        {/* Barre de progression */}
                                        <div 
                                          className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                            result.luckRating <= 1 ? 'bg-red-500' : 
                                            result.luckRating <= 2 ? 'bg-yellow-500' : 
                                            result.luckRating <= 3 ? 'bg-green-500' : 'bg-blue-500'
                                          }`}
                                          style={{ width: `${progressPercentage}%` }}
                                        ></div>
                                      </div>
                                      <div className="flex text-xs justify-between">
                                        <span className="text-red-500 font-medium">{t('pokemon_luck.results.luck_scale.unlucky')}</span>
                                        <span className="text-yellow-500 font-medium">{t('pokemon_luck.results.luck_scale.average')}</span>
                                        <span className="text-green-500 font-medium">{t('pokemon_luck.results.luck_scale.lucky')}</span>
                                        <span className="text-blue-500 font-medium">{t('pokemon_luck.results.luck_scale.incredible')}</span>
                                      </div>
                                    </div>
                                    
                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                      <div className="bg-gray-50 p-2 rounded">
                                        <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.probability_less')}</div>
                                        <div className="text-sm font-medium text-gray-900">{(result.lessThanProbability * 100).toFixed(2)}%</div>
                                      </div>
                                      <div className="bg-gray-50 p-2 rounded">
                                        <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.probability_more')}</div>
                                        <div className="text-sm font-medium text-gray-900">{(result.moreThanProbability * 100).toFixed(2)}%</div>
                                      </div>
                                      <div className="bg-gray-50 p-2 rounded">
                                        <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.packs_expected')}</div>
                                        <div className="text-sm font-medium text-gray-900">{result.expectedPacks.toFixed(0)}</div>
                                      </div>
                                      <div className="bg-gray-50 p-2 rounded">
                                        <div className="text-xs text-gray-500 mb-1">{t('pokemon_luck.results.ratio_per_pack')}</div>
                                        <div className="text-sm font-medium text-gray-900">{(formData[key] / formData.packsOpened).toFixed(3)}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {showLeaderboard && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
                  {t('pokemon_luck.leaderboard.title')}
                </h3>
                
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.rank')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.player')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.score')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.rating')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.packs')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                          {t('pokemon_luck.leaderboard.columns.date')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('pokemon_luck.leaderboard.columns.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leaderboard.map((score) => (
                        <tr key={score.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {score.rank <= 3 ? (
                                <span className="flex items-center">
                                  {score.rank === 1 && "🥇"}
                                  {score.rank === 2 && "🥈"}
                                  {score.rank === 3 && "🥉"}
                                  {score.rank}
                                </span>
                              ) : (
                                score.rank
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {score.avatar_url ? (
                                <img 
                                  src={score.avatar_url} 
                                  alt={`Avatar de ${score.username}`}
                                  className="h-8 w-8 rounded-full mr-3 object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                                  <Users className="h-4 w-4 text-gray-500" />
                                </div>
                              )}
                              <Link 
                                to={getPathForLang(`/users/${score.username}`)}
                                className="text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors"
                              >
                                {score.username}
                              </Link>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{score.score ? score.score.toFixed(2) : '-'}/100</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {(() => {
                                // Fonction pour convertir le texte de rating en clé de traduction
                                const getRatingKey = (ratingText: string) => {
                                  // Si le texte est vide ou null/undefined, utiliser la valeur par défaut
                                  if (!ratingText) return 'average';
                                  
                                  const text = ratingText.toLowerCase();
                                  
                                  // Correspondances exactes basées sur les anciennes évaluations
                                  if (text === 'excellente' || text === 'excellent') return 'incredibly_lucky';
                                  if (text === 'très bonne' || text === 'très bon') return 'very_lucky';
                                  if (text === 'bonne' || text === 'bon') return 'lucky';
                                  if (text === 'dans la moyenne') return 'average';
                                  if (text === 'en dessous de la moyenne') return 'unlucky';
                                  if (text === 'mauvaise' || text === 'mauvais') return 'very_unlucky';
                                  if (text === 'catastrophique') return 'very_unlucky';
                                  
                                  // Correspondances par mots-clés pour les cas non exacts
                                  if (text.includes('excel') || text.includes('except') || text.includes('perfect')) return 'incredibly_lucky';
                                  if (text.includes('très') && (text.includes('bonne') || text.includes('bon'))) return 'very_lucky';
                                  if (text.includes('bonne') || text.includes('bon') || text.includes('au-dessus')) return 'lucky';
                                  if (text.includes('moyenne') && !text.includes('dessous')) return 'average';
                                  if (text.includes('dessous') || text.includes('below')) return 'unlucky';
                                  if (text.includes('mauvais') || text.includes('catast')) return 'very_unlucky';
                                  
                                  // Cas spécifiques en anglais
                                  if (text.includes('very') && text.includes('lucky')) return 'very_lucky';
                                  if (text.includes('lucky') && !text.includes('unlucky')) return 'lucky';
                                  if (text.includes('average')) return 'average';
                                  if (text.includes('unlucky')) return 'unlucky';
                                  if (text.includes('very') && text.includes('unlucky')) return 'very_unlucky';
                                  
                                  // Scores traduits directement
                                  const scoreMapping: Record<number, string> = {
                                    0: 'very_unlucky',    // 0-30: Mauvaise
                                    1: 'unlucky',         // 30-40: En dessous de la moyenne
                                    2: 'average',         // 40-50: Dans la moyenne
                                    3: 'lucky',           // 50-60: Très bonne
                                    4: 'incredibly_lucky' // 60+: Excellente
                                  };
                                  
                                  // Si score.rating est disponible, l'utiliser comme fallback
                                  if (score.rating !== undefined) {
                                    const ratingAsNum = Math.floor(score.rating);
                                    if (scoreMapping[ratingAsNum]) {
                                      return scoreMapping[ratingAsNum];
                                    }
                                  }
                                  
                                  // Si le score est disponible, essayer de déterminer l'évaluation basée sur le score
                                  if (score.score !== undefined) {
                                    if (score.score >= 60) return 'incredibly_lucky';  // Excellente
                                    if (score.score >= 50) return 'lucky';             // Très bonne
                                    if (score.score >= 40) return 'average';           // Dans la moyenne
                                    if (score.score >= 30) return 'unlucky';           // En dessous de la moyenne
                                    if (score.score >= 0) return 'very_unlucky';       // Mauvaise
                                  }
                                  
                                  // Valeur par défaut si rien ne correspond
                                  console.log('Cas non géré pour le rating:', text);
                                  return 'average';
                                };
                                
                                return t(`pokemon_luck.leaderboard.ratings.${getRatingKey(score.rating_text)}`);
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{score.packs_opened}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 lg:table-cell hidden">
                            {new Date(score.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleViewScoreDetails(score.id)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <span className="hidden sm:inline">{t('pokemon_luck.leaderboard.details')}</span>
                              <span className="sm:hidden">📊</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>

      {showPublishModal && <PublishScoreModal />}
      
      {/* Afficher le modal de détail du score si un score est sélectionné */}
      {selectedScore && (
        <ScoreDetailModal
          score={selectedScore}
          onClose={() => setSelectedScore(null)}
        />
      )}
    </PageLayout>
  );
};

export default PokemonLuck; 